import type {
  PromoteResult,
  PromotionDrift,
  PromotionRecord,
  StagePosition,
  BackPromoteFanOutResult,
  BackPromoteCascadeResult,
} from '../../types/promotion.types'
import type { IPromotionService, PromoteBody, BackPromoteFanOutBody, BackPromoteCascadeBody } from './IPromotionService.interface'

// ── Canned drift gate ─────────────────────────────────────────────────────────
// Fired when a promote targets a setSlug containing 'drift' without driftAck.

const MOCK_DRIFT: PromotionDrift = {
  comparisonId: 'cmp-mock',
  summary:      { added: 1, changed: 0, deleted: 0, unchanged: 5 },
  components:   [{ type: 'ApexClass', fullName: 'Drifted', change: 'added' }],
}

/**
 * In-memory, deterministic promotion service. Nothing persists across instances.
 * Records are kept per pipeline slug; positions are derived from succeeded records.
 */
export class MockPromotionService implements IPromotionService {
  private readonly bySlug = new Map<string, PromotionRecord[]>()
  private seq = 0

  promote(slug: string, body: PromoteBody): Promise<PromoteResult> {
    return this._promote(slug, body, 'forward')
  }

  backPromote(slug: string, body: PromoteBody): Promise<PromoteResult> {
    return this._promote(slug, body, 'backward')
  }

  /** Deterministic fan-out: two feeders — one applied, one skipped-identical. */
  async backPromoteFanOut(slug: string, body: BackPromoteFanOutBody): Promise<BackPromoteFanOutResult> {
    const cascadeId = `csc-mock-${++this.seq}`
    const applied = await this._promote(slug, { transitionId: `${body.originStageId}<-dev-a`, setSlug: body.setSlug, setVersion: body.setVersion }, 'backward')
    return {
      cascadeId,
      results: [
        { stageId: 'dev-a', stageName: 'dev-a', outcome: 'applied', ...(applied.record && { record: { ...applied.record, cascadeId } }) },
        { stageId: 'dev-b', stageName: 'dev-b', outcome: 'skipped-identical' },
      ],
    }
  }

  /** Deterministic 2-hop cascade: uat applied, then sit skipped-identical; not paused. */
  async backPromoteCascade(slug: string, body: BackPromoteCascadeBody): Promise<BackPromoteCascadeResult> {
    const cascadeId = `csc-mock-${++this.seq}`
    const hop = await this._promote(slug, { transitionId: `${body.originStageId}-cascade`, setSlug: body.setSlug, setVersion: body.setVersion }, 'backward')
    return {
      cascadeId,
      paused: false,
      hops: [
        { fromStageId: body.originStageId, fromStageName: body.originStageId, toStageId: 'uat', toStageName: 'uat', outcome: 'applied', ...(hop.record && { record: { ...hop.record, cascadeId } }) },
        { fromStageId: 'uat', fromStageName: 'uat', toStageId: 'sit', toStageName: 'sit', outcome: 'skipped-identical' },
      ],
    }
  }

  private _promote(slug: string, body: PromoteBody, direction: 'forward' | 'backward'): Promise<PromoteResult> {
    // Drift gate: a set whose slug mentions 'drift' must be acknowledged.
    if (body.setSlug.includes('drift') && !body.driftAck) {
      return Promise.resolve({ needsAck: true, drift: MOCK_DRIFT })
    }

    const n = ++this.seq
    const now = new Date().toISOString()
    // Backward reverses the edge (origin = transition.to, target = transition.from).
    const [fromStageId, toStageId] = direction === 'backward'
      ? [`to-${body.transitionId}`, `from-${body.transitionId}`]
      : [`from-${body.transitionId}`, `to-${body.transitionId}`]
    const record: PromotionRecord = {
      id:                 `promo-${n}`,
      projectId:          'default',
      pipelineSlug:       slug,
      transitionId:       body.transitionId,
      fromStageId,
      toStageId,
      ...(direction === 'backward' && { direction }),
      setSlug:            body.setSlug,
      setVersion:         body.setVersion,
      commitSha:          `sha-${n}`,
      repo:               'acme/sfdx',
      gitConnectionId:    'conn-git',
      prNumber:           1000 + n,
      prUrl:              `https://github.com/acme/sfdx/pull/${1000 + n}`,
      targetConnectionId: `conn-target-${body.transitionId}`,
      driftOverride:      !!body.driftAck,
      status:             'pr-open',
      createdAt:          now,
      updatedAt:          now,
      ...(body.driftAck === true && { driftComparisonId: MOCK_DRIFT.comparisonId }),
      ...(body.author !== undefined && { author: body.author }),
    }

    const list = this.bySlug.get(slug) ?? []
    list.push(record)
    this.bySlug.set(slug, list)

    return Promise.resolve({ needsAck: false, record: { ...record } })
  }

  merge(
    slug: string,
    promotionId: string,
  ): Promise<{ merged: boolean; deployRunId: string; record: PromotionRecord }> {
    const list = this.bySlug.get(slug)
    const record = list?.find((r) => r.id === promotionId)
    if (!record) return Promise.reject(new Error(`Promotion '${promotionId}' not found`))

    const deployRunId = `run-mock-${record.id}`
    record.status = 'deploying'
    record.deployRunId = deployRunId
    record.mergedSha = `merged-${record.commitSha}`
    record.updatedAt = new Date().toISOString()

    return Promise.resolve({ merged: true, deployRunId, record: { ...record } })
  }

  listPromotions(slug: string): Promise<PromotionRecord[]> {
    const list = this.bySlug.get(slug) ?? []
    // Newest first. Records are appended in creation order, so reversing gives
    // newest-first and remains stable when timestamps tie (same millisecond).
    const sorted = [...list].reverse()
    return Promise.resolve(sorted.map((r) => ({ ...r })))
  }

  positions(slug: string): Promise<StagePosition[]> {
    const list = this.bySlug.get(slug) ?? []
    const latest = new Map<string, PromotionRecord>()
    for (const r of list) {
      if (r.status !== 'succeeded') continue
      const prev = latest.get(r.toStageId)
      if (!prev || new Date(r.updatedAt).getTime() >= new Date(prev.updatedAt).getTime()) {
        latest.set(r.toStageId, r)
      }
    }
    const positions: StagePosition[] = [...latest.values()].map((r) => ({
      stageId:     r.toStageId,
      setSlug:     r.setSlug,
      setVersion:  r.setVersion,
      commitSha:   r.commitSha,
      promotedAt:  r.updatedAt,
      promotionId: r.id,
    }))
    return Promise.resolve(positions)
  }
}
